/**
 * The guess a player is part-way through building, kept across a reload.
 *
 * Memory is not a side effect of pinning a peg, it is the whole of what the
 * gesture means: a note saying which peg belongs where, that a refresh wiped,
 * would not be a note at all. The same goes for the row built while the
 * opponent is still thinking, which is most of the time a turn-based game
 * spends.
 *
 * So what is kept here is the player's own working state, and the four things
 * that keep it that rather than a record of them are structural, not promises:
 * only pegs their own hand placed, keyed by a game they are already in so no
 * new identifier is minted, never sent anywhere, and thrown away with the game
 * it belongs to. That is what the exemption /privacy relies on rests on, and
 * each of the four is a test.
 */

import { CODE_LENGTH } from './pegs.js';
import { persist, readJSON } from './storage.js';

/**
 * The one key every board's draft is kept under, holding a list of them.
 *
 * One key rather than one per game, for the reason TUTORIALS_KEY gives: it
 * costs one clause on the privacy page rather than a family of them, and
 * forgetting the lot is a single delete.
 */
export const DRAFTS_KEY = 'unkoad.drafts';

/**
 * How many boards' drafts are kept, oldest dropped first.
 *
 * The cap is not housekeeping. Working state that piled up across every game a
 * browser had ever opened would stop being working state and become a record
 * of what its owner has been playing, which is the thing this must never be.
 * Twenty is comfortably more than anyone has on the go at once.
 */
export const DRAFT_LIMIT = 20;

/**
 * The draft kept for a board, or null where there is nothing to restore.
 *
 * Everything read back is checked against the palette in the player's hand, so
 * a mode that has been retired, the forfeit row's pegs, and anything a version
 * of the game that stored something else here left behind all come back as an
 * empty socket rather than as a peg the board would refuse to play.
 */
export function loadDraft(storage, key, palette = []) {
    if (!key) return null;

    const found = entries(storage).find((entry) => entry.key === key);
    return found ? clean(found, palette) : null;
}

/**
 * Puts a board's draft away, as the newest of them.
 *
 * A row with nothing in it is dropped rather than stored: opening a board and
 * touching nothing must leave nothing behind.
 */
export function saveDraft(storage, key, { guess = [], locks = [] } = {}) {
    if (!storage || !key) return;
    if (![...guess, ...locks].some(Boolean)) return dropDraft(storage, key);

    const kept = entries(storage).filter((entry) => entry.key !== key);
    kept.push({ key, guess: [...guess], locks: [...locks] });
    write(storage, kept.slice(-DRAFT_LIMIT));
}

/** Forgets a board's draft, which is what the game ending does. */
export function dropDraft(storage, key) {
    if (!storage || !key) return;

    const on = entries(storage);
    const kept = on.filter((entry) => entry.key !== key);
    // Nothing to forget writes nothing. Otherwise opening a board and touching
    // it once would leave an empty list behind, which is a thing stored for a
    // player who never asked the game to remember anything.
    if (kept.length !== on.length) write(storage, kept);
}

function write(storage, kept) {
    persist(storage, DRAFTS_KEY, JSON.stringify(kept));
}

/** The drafts on record, with anything of the wrong shape passed over. */
function entries(storage) {
    const parsed = readJSON(storage, DRAFTS_KEY);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((entry) => (
        entry
        && typeof entry === 'object'
        && typeof entry.key === 'string'
        && entry.key !== ''
        && Array.isArray(entry.guess)
        && Array.isArray(entry.locks)
    ));
}

/** One entry as a board can use it, or null if nothing of it survived. */
function clean(entry, palette) {
    const guess = pegRow(entry.guess, palette);
    const locks = pegRow(entry.locks, palette);

    // A note survives only while its socket still holds the peg it names. It is
    // the rule settle() keeps, applied again to what comes back, so a store
    // that was edited by hand cannot produce a pin the board could not have
    // made itself.
    for (let i = 0; i < CODE_LENGTH; i++) {
        if (locks[i] !== guess[i]) locks[i] = '';
    }

    return guess.some(Boolean) ? { guess, locks } : null;
}

/** A stored row as five sockets, holding only pegs this board actually offers. */
function pegRow(codes, palette) {
    const out = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < CODE_LENGTH; i++) {
        if (typeof codes[i] === 'string' && palette.includes(codes[i])) out[i] = codes[i];
    }
    return out;
}
